import { GridStack, type GridItemHTMLElement, type GridStackNode, type GridStackOptions } from 'gridstack';
import 'gridstack/dist/gridstack.min.css';
import './dashboard-grid.css';

export type DashboardProfile = 'desktop' | 'mobile';

export interface DashboardGridOptions {
  readonly profile: DashboardProfile;
  readonly editing: boolean;
  readonly columns: 4 | 12;
  readonly maxRows: number;
  readonly breakpoint: 992;
}

export interface DashboardGridChange {
  readonly id: string;
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

export interface DotNetDashboardReference {
  invokeMethodAsync(method: string, ...args: readonly unknown[]): Promise<unknown>;
}

export interface DashboardGridHandle {
  sync(options: DashboardGridOptions): void;
  dispose(): void;
}

interface DashboardLayoutSubmission {
  readonly sequence: number;
  readonly generation: number;
  readonly profile: DashboardProfile;
  readonly changes: readonly DashboardGridChange[];
}

interface GridFacade {
  readonly engine: { readonly nodes: readonly GridStackNode[] };
  on(name: 'change', callback: (event: Event, items: GridStackNode[]) => void): GridFacade;
  offAll(): GridFacade;
  destroy(removeDom?: boolean): GridFacade;
  setStatic(value: boolean): GridFacade;
  setAnimation(value: boolean): GridFacade;
  column(columns: number, layout?: 'none'): GridFacade;
  makeWidget(element: HTMLElement): GridItemHTMLElement;
  update(element: HTMLElement, options: GridStackNode): GridFacade;
  load?(items: GridStackNode[], addRemove?: boolean): GridFacade;
  batchUpdate?(flag?: boolean): GridFacade;
  isIgnoreChangeCB(): boolean;
}

export type DashboardGridFactory = (options: GridStackOptions, root: HTMLElement) => GridFacade;

const defaultFactory: DashboardGridFactory = (options, root) => {
  const grid = GridStack.init(options, root);
  if (grid === null) {
    throw new Error('Dashboard GridStack root could not be initialized.');
  }
  return grid;
};

export function validateDashboardGridOptions(value: unknown): DashboardGridOptions {
  if (!isRecord(value)) {
    throw new TypeError('Dashboard grid options must be an object.');
  }

  const profile = value['profile'];
  const editing = value['editing'];
  const columns = value['columns'];
  const maxRows = value['maxRows'];
  const breakpoint = value['breakpoint'];
  if ((profile !== 'desktop' && profile !== 'mobile')
    || typeof editing !== 'boolean'
    || (columns !== 4 && columns !== 12)
    || !Number.isInteger(maxRows)
    || typeof maxRows !== 'number'
    || maxRows <= 0
    || breakpoint !== 992
    || (profile === 'desktop' && columns !== 12)
    || (profile === 'mobile' && columns !== 4)) {
    throw new TypeError('Dashboard grid options are invalid.');
  }

  return { profile, editing, columns, maxRows, breakpoint };
}

export function validateDashboardGridChange(
  value: unknown,
  options: DashboardGridOptions
): DashboardGridChange {
  if (!isRecord(value)) {
    throw new TypeError('Dashboard grid change must be an object.');
  }

  const id = value['id'];
  const x = value['x'];
  const y = value['y'];
  const width = value['width'];
  const height = value['height'];
  if (typeof id !== 'string'
    || id.trim().length === 0
    || !isNonNegativeInteger(x)
    || !isNonNegativeInteger(y)
    || !isPositiveInteger(width)
    || !isPositiveInteger(height)
    || x + width > options.columns
    || y + height > options.maxRows) {
    throw new TypeError('Dashboard grid geometry is invalid.');
  }

  return { id: id.trim(), x, y, width, height };
}

export function isSupportedLayoutInput(event: Event): boolean {
  return (typeof PointerEvent !== 'undefined' && event instanceof PointerEvent)
    || (typeof TouchEvent !== 'undefined' && event instanceof TouchEvent)
    || event instanceof MouseEvent;
}

export class DashboardGridAdapter implements DashboardGridHandle {
  private options: DashboardGridOptions;
  private grid: GridFacade;
  private disposed = false;
  private suppressGridChanges = false;
  private flushingLayout = false;
  private layoutSequence = 0;
  private layoutGeneration = 0;
  private widgetKeys = '';
  private blazorWidgetOrder: HTMLElement[] = [];
  private pendingLayout: DashboardLayoutSubmission | undefined;
  private readonly acceptedLayouts = new Map<DashboardProfile, readonly DashboardGridChange[]>();
  private readonly intersectionObserver: IntersectionObserver;
  private readonly reducedMotion: MediaQueryList;
  private readonly breakpoint: MediaQueryList;

  public constructor(
    private readonly root: HTMLElement,
    options: DashboardGridOptions,
    private readonly dotNet: DotNetDashboardReference,
    private readonly factory: DashboardGridFactory = defaultFactory
  ) {
    this.options = validateDashboardGridOptions(options);
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.breakpoint = window.matchMedia(`(min-width: ${this.options.breakpoint}px)`);
    this.grid = this.createGrid();
    this.intersectionObserver = new IntersectionObserver(this.onIntersection, {
      root: null,
      rootMargin: '400px 0px',
      threshold: 0
    });

    document.addEventListener('visibilitychange', this.onVisibilityChange);
    document.addEventListener('dragstart', this.onCatalogDragStart);
    this.root.addEventListener('dragover', this.onCatalogDragOver);
    this.root.addEventListener('drop', this.onCatalogDrop);
    this.reducedMotion.addEventListener('change', this.onReducedMotionChange);
    this.breakpoint.addEventListener('change', this.onBreakpointChange);
    this.sync(this.options);
    this.onVisibilityChange();
    this.notifyInitialBreakpoint();
  }

  public sync(options: DashboardGridOptions): void {
    this.ensureActive();
    const previousOptions = this.options;
    this.options = validateDashboardGridOptions(options);
    this.blazorWidgetOrder = this.gridItems();
    const nextWidgetKeys = this.readWidgetKeys(this.blazorWidgetOrder);
    const widgetKeysChanged = this.widgetKeys.length > 0 && nextWidgetKeys !== this.widgetKeys;
    const contextChanged = previousOptions.profile !== this.options.profile
      || previousOptions.editing !== this.options.editing
      || widgetKeysChanged;
    if (contextChanged) {
      this.layoutGeneration++;
      this.pendingLayout = undefined;
    }
    const preservePendingLayout = !contextChanged
      && (this.flushingLayout || this.pendingLayout !== undefined);
    if (widgetKeysChanged) {
      this.grid.offAll();
      this.grid.destroy(false);
      this.grid = this.createGrid();
    }
    this.widgetKeys = nextWidgetKeys;

    this.suppressGridChanges = true;
    try {
      this.grid.batchUpdate?.();
      try {
        this.grid.column(this.options.columns, 'none');
        this.grid.setStatic(!this.options.editing);
        this.applyReducedMotion();
        this.root.style.setProperty('--dashboard-grid-columns', String(this.options.columns));
        this.root.dataset['dashboardProfile'] = this.options.profile;
      } finally {
        this.grid.batchUpdate?.(false);
      }

      for (const element of this.blazorWidgetOrder) {
        const gridElement = element as GridItemHTMLElement;
        if (gridElement.gridstackNode === undefined) {
          this.grid.makeWidget(element);
        }
      }
      if (!preservePendingLayout) {
        this.loadLayout(this.blazorWidgetOrder.map(element => this.readGeometry(element)));
        this.acceptedLayouts.set(this.options.profile, this.readEngineSnapshot());
      }
    } finally {
      this.suppressGridChanges = false;
      this.restoreBlazorWidgetOrder();
    }
    this.observeWidgetBodies();
  }

  public dispose(): void {
    if (this.disposed) {
      return;
    }
    this.disposed = true;
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    document.removeEventListener('dragstart', this.onCatalogDragStart);
    this.root.removeEventListener('dragover', this.onCatalogDragOver);
    this.root.removeEventListener('drop', this.onCatalogDrop);
    this.reducedMotion.removeEventListener('change', this.onReducedMotionChange);
    this.breakpoint.removeEventListener('change', this.onBreakpointChange);
    this.intersectionObserver.disconnect();
    this.grid.offAll();
    this.grid.destroy(false);
  }

  private createGrid(): GridFacade {
    const grid = this.factory({
      column: this.options.columns,
      maxRow: this.options.maxRows,
      cellHeight: 'auto',
      margin: 6,
      float: false,
      animate: !this.reducedMotion?.matches,
      staticGrid: !this.options.editing,
      draggable: {
        handle: '.dashboard-tile__drag-handle',
        scroll: true
      },
      resizable: {
        handles: 'e,se,s,sw,w'
      }
    }, this.root);
    grid.on('change', this.onGridChange);
    return grid;
  }

  private readonly onGridChange = (): void => {
    if (this.disposed
      || this.suppressGridChanges
      || !this.options.editing
      || this.grid.isIgnoreChangeCB()) {
      return;
    }

    try {
      this.pendingLayout = {
        sequence: ++this.layoutSequence,
        generation: this.layoutGeneration,
        profile: this.options.profile,
        changes: this.readEngineSnapshot()
      };
      void this.flushLayoutChanges();
    } catch {
      this.rejectCurrentLayout(this.options.profile);
    } finally {
      queueMicrotask(() => this.restoreBlazorWidgetOrder());
    }
  };

  private async flushLayoutChanges(): Promise<void> {
    if (this.flushingLayout) {
      return;
    }

    this.flushingLayout = true;
    try {
      while (!this.disposed && this.pendingLayout !== undefined) {
        const submission = this.pendingLayout;
        this.pendingLayout = undefined;
        try {
          const result = await this.dotNet.invokeMethodAsync(
            'OnDashboardLayoutChanged',
            submission.sequence,
            submission.profile,
            submission.changes);
          if (submission.generation !== this.layoutGeneration) {
            continue;
          }
          if (result === 'accepted') {
            this.acceptedLayouts.set(submission.profile, submission.changes);
          } else if (result === 'rejected') {
            this.pendingLayout = undefined;
            this.rejectCurrentLayout(submission.profile);
          }
        } catch {
          if (submission.generation !== this.layoutGeneration) {
            continue;
          }
          this.pendingLayout = undefined;
          this.rejectCurrentLayout(submission.profile);
        }
      }
    } finally {
      this.flushingLayout = false;
    }
  }

  private readonly onIntersection: IntersectionObserverCallback = entries => {
    if (this.disposed) {
      return;
    }
    for (const entry of entries) {
      const key = (entry.target as HTMLElement).dataset['dashboardWidgetBody'];
      if (key !== undefined) {
        void this.dotNet.invokeMethodAsync('OnDashboardWidgetNearViewport', key, entry.isIntersecting);
      }
    }
  };

  private readonly onVisibilityChange = (): void => {
    if (!this.disposed) {
      void this.dotNet.invokeMethodAsync('OnDashboardVisibilityChanged', document.visibilityState !== 'hidden');
    }
  };

  private readonly onReducedMotionChange = (): void => this.applyReducedMotion();

  private readonly onBreakpointChange = (): void => {
    if (!this.disposed) {
      const profile: DashboardProfile = this.breakpoint.matches ? 'desktop' : 'mobile';
      void this.dotNet.invokeMethodAsync('OnDashboardProfileBreakpointChanged', profile);
    }
  };

  private readonly onCatalogDragStart = (event: DragEvent): void => {
    const target = event.target instanceof Element
      ? event.target.closest<HTMLElement>('[data-dashboard-catalog-key]')
      : null;
    const key = target?.dataset['dashboardCatalogKey'];
    if (key !== undefined && event.dataTransfer !== null) {
      event.dataTransfer.effectAllowed = 'copy';
      event.dataTransfer.setData('application/x-mathesio-dashboard-widget', key);
    }
  };

  private readonly onCatalogDragOver = (event: DragEvent): void => {
    if (event.dataTransfer?.types.includes('application/x-mathesio-dashboard-widget') === true) {
      event.preventDefault();
      event.dataTransfer.dropEffect = 'copy';
    }
  };

  private readonly onCatalogDrop = (event: DragEvent): void => {
    const key = event.dataTransfer?.getData('application/x-mathesio-dashboard-widget').trim();
    if (key !== undefined && key.length > 0) {
      event.preventDefault();
      void this.dotNet.invokeMethodAsync('OnDashboardCatalogDrop', key);
    }
  };

  private notifyInitialBreakpoint(): void {
    const expected: DashboardProfile = this.breakpoint.matches ? 'desktop' : 'mobile';
    if (expected !== this.options.profile) {
      void this.dotNet.invokeMethodAsync('OnDashboardProfileBreakpointChanged', expected);
    }
  }

  private applyReducedMotion(): void {
    if (this.disposed) {
      return;
    }
    const reduced = this.reducedMotion.matches;
    this.grid.setAnimation(!reduced);
    this.root.classList.toggle('dashboard-grid--reduced-motion', reduced);
  }

  private observeWidgetBodies(): void {
    this.intersectionObserver.disconnect();
    for (const element of this.root.querySelectorAll<HTMLElement>('[data-dashboard-widget-body]')) {
      this.intersectionObserver.observe(element);
    }
  }

  private readWidgetKeys(elements: readonly HTMLElement[]): string {
    return elements
      .map(element => element.dataset['dashboardWidgetKey'] ?? '')
      .sort((left, right) => left.localeCompare(right))
      .join('\u001f');
  }

  private gridItems(): HTMLElement[] {
    return Array.from(this.root.children)
      .filter((element): element is HTMLElement => element instanceof HTMLElement && element.classList.contains('grid-stack-item'));
  }

  private readGeometry(element: HTMLElement): DashboardGridChange {
    return validateDashboardGridChange({
      id: element.getAttribute('gs-id'),
      x: Number(element.getAttribute('gs-x')),
      y: Number(element.getAttribute('gs-y')),
      width: Number(element.getAttribute('gs-w')),
      height: Number(element.getAttribute('gs-h'))
    }, this.options);
  }

  private readEngineSnapshot(): readonly DashboardGridChange[] {
    const changes = this.grid.engine.nodes.map(node => validateDashboardGridChange({
      id: node.id,
      x: node.x,
      y: node.y,
      width: node.w,
      height: node.h
    }, this.options));
    const uniqueKeys = new Set(changes.map(change => change.id.toLowerCase()));
    if (changes.length !== this.blazorWidgetOrder.length || uniqueKeys.size !== changes.length) {
      throw new Error('Dashboard grid engine snapshot does not match rendered widgets.');
    }
    return changes.sort((left, right) => left.id.localeCompare(right.id));
  }

  private loadLayout(changes: readonly DashboardGridChange[]): void {
    const nodes = changes.map(change => ({
      id: change.id,
      x: change.x,
      y: change.y,
      w: change.width,
      h: change.height,
      noMove: !this.options.editing,
      noResize: !this.options.editing
    }));
    if (this.grid.load !== undefined) {
      this.grid.load(nodes, false);
      return;
    }
    throw new Error('Dashboard GridStack adapter requires atomic layout loading.');
  }

  private rollbackLayout(profile: DashboardProfile): void {
    if (profile !== this.options.profile) {
      return;
    }
    const accepted = this.acceptedLayouts.get(profile);
    if (accepted === undefined) {
      return;
    }

    this.suppressGridChanges = true;
    try {
      this.loadLayout(accepted);
    } finally {
      this.suppressGridChanges = false;
      this.restoreBlazorWidgetOrder();
    }
  }

  private rejectCurrentLayout(profile: DashboardProfile): void {
    this.rollbackLayout(profile);
    void this.dotNet.invokeMethodAsync('OnDashboardLayoutSyncFailed').catch(() => undefined);
  }

  private restoreBlazorWidgetOrder(): void {
    if (this.disposed) {
      return;
    }
    for (const element of this.blazorWidgetOrder) {
      if (element.parentElement === this.root) {
        this.root.appendChild(element);
      }
    }
  }

  private ensureActive(): void {
    if (this.disposed) {
      throw new Error('Dashboard GridStack adapter is disposed.');
    }
  }
}

export function createDashboardGrid(
  root: HTMLElement,
  options: DashboardGridOptions,
  dotNet: DotNetDashboardReference
): DashboardGridHandle {
  if (!(root instanceof HTMLElement)) {
    throw new TypeError('Dashboard grid root must be an HTMLElement.');
  }
  if (!isDotNetReference(dotNet)) {
    throw new TypeError('Dashboard grid requires a .NET callback reference.');
  }
  return new DashboardGridAdapter(root, options, dotNet);
}

function isDotNetReference(value: unknown): value is DotNetDashboardReference {
  return isRecord(value) && typeof value['invokeMethodAsync'] === 'function';
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isNonNegativeInteger(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0;
}

function isPositiveInteger(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value > 0;
}
